import axios from '@axios'
import config from '@core/app-config/api-config'
import {
  PHOTO_SHORTCODE_REGEX,
  describePhotoShortcode,
} from '@/utils/photo-shortcode'

class PhotoShortcodePreview {
  constructor(quill, options = {}) {
    this.quill = quill
    this.options = options
    this.isSyncing = false
    this.previewCache = new Map()
    this.syncTimer = null

    this.quill.on('text-change', () => {
      clearTimeout(this.syncTimer)
      this.syncTimer = setTimeout(() => this.syncShortcodes(), 120)
    })

    setTimeout(() => this.syncShortcodes(), 0)
  }

  collectReplacements() {
    const contents = this.quill.getContents()
    const replacements = []
    let index = 0

    contents.ops.forEach(op => {
      if (typeof op.insert === 'string') {
        const regex = new RegExp(PHOTO_SHORTCODE_REGEX.source, 'g')
        let match = regex.exec(op.insert)

        while (match) {
          replacements.push({
            index: index + match.index,
            length: match[0].length,
            shortcode: match[0],
          })
          match = regex.exec(op.insert)
        }

        index += op.insert.length
        return
      }

      if (op.insert?.['photo-shortcode']) {
        index += 1
        return
      }

      index += 1
    })

    return replacements
  }

  async resolvePreviewMeta(shortcode) {
    if (this.previewCache.has(shortcode)) {
      return this.previewCache.get(shortcode)
    }

    const match = shortcode.match(/^\[\[(photo|photos):([^\]]+)\]\]$/)
    if (!match) {
      const fallback = { shortcode, kind: 'photos', label: shortcode }
      this.previewCache.set(shortcode, fallback)
      return fallback
    }

    const kind = match[1]
    const inner = match[2].trim().split('@start:')[0].trim()

    if (kind === 'photo') {
      try {
        const { data } = await axios.get(`${config.host}/album/photo/${inner}`)
        const meta = {
          shortcode,
          kind: 'photo',
          previewUrl: data?.file?.fullPath || '',
          label: `Фото #${inner}`,
        }
        this.previewCache.set(shortcode, meta)
        return meta
      } catch (error) {
        const meta = {
          shortcode,
          kind: 'photo',
          label: `Фото #${inner}`,
        }
        this.previewCache.set(shortcode, meta)
        return meta
      }
    }

    const meta = {
      shortcode,
      kind: 'photos',
      label: describePhotoShortcode(shortcode),
    }
    this.previewCache.set(shortcode, meta)
    return meta
  }

  async syncShortcodes() {
    if (this.isSyncing) {
      return
    }

    const replacements = this.collectReplacements()
    if (!replacements.length) {
      return
    }

    this.isSyncing = true

    try {
      const metas = await Promise.all(
        replacements.map(item => this.resolvePreviewMeta(item.shortcode)),
      )

      replacements
        .slice()
        .reverse()
        .forEach((item, reverseIndex) => {
          const meta = metas[replacements.length - 1 - reverseIndex]
          this.quill.deleteText(item.index, item.length, 'silent')
          this.quill.insertEmbed(item.index, 'photo-shortcode', meta, 'silent')
        })
    } finally {
      this.isSyncing = false
    }
  }
}

export default PhotoShortcodePreview
